import React, { useState, useMemo } from 'react';
import { Wrench, Droplets, CheckCircle2, AlertTriangle, Search, Zap, ShieldCheck, Loader2, X, FileText } from 'lucide-react';
import { Product } from './types';
import { Card, Badge, StatCard } from './SharedComponents';

export const MaintenancePage = ({ products, onReleaseFromMaintenance }: { products: Product[], onReleaseFromMaintenance: (productId: string, qty: number) => void }) => {
  const [search, setSearch] = useState('');
  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const maintenanceItems = useMemo(() => {
    return products.filter(p => p.quantityMaintenance > 0 && p.name.toLowerCase().includes(search.toLowerCase()));
  }, [products, search]);

  const stats = useMemo(() => ({
    totalItems: products.reduce((acc, p) => acc + p.quantityMaintenance, 0),
    totalSKUs: products.filter(p => p.quantityMaintenance > 0).length,
  }), [products]);

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20 p-4 md:p-6 lg:p-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div className="space-y-1">
          <h1 className="text-2xl md:text-3xl font-black text-secondary uppercase tracking-tighter flex items-center gap-3">
             <Wrench size={28} className="text-amber-500" /> Oficina Técnica
          </h1>
          <p className="text-gray-400 text-xs md:text-sm font-medium">Controle de materiais em quarentena.</p>
        </div>
        <button onClick={() => setIsReportModalOpen(true)} className="w-full md:w-auto px-6 py-3.5 bg-white border-2 border-gray-100 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-gray-50 shadow-sm transition-all">
          Gerar Relatório
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        <StatCard title="Itens na Oficina" value={stats.totalItems} icon={Wrench} colorClass="bg-amber-50 text-amber-600" />
        <StatCard title="SKUs em Alerta" value={stats.totalSKUs} icon={AlertTriangle} colorClass="bg-red-50 text-red-600" />
        <StatCard title="Status Operacional" value="ATIVO" icon={Zap} colorClass="bg-blue-50 text-blue-600" />
      </div>

      <Card className="p-4 md:p-8 space-y-6 bg-white border-none shadow-xl rounded-[32px]">
        <div className="relative group">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={20} />
          <input 
            type="text" 
            placeholder="Pesquisar material..." 
            className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-[20px] outline-none font-bold text-secondary text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {maintenanceItems.length > 0 ? maintenanceItems.map(p => (
            <div key={p.id} className="bg-white border-2 border-gray-50 rounded-[28px] p-5 hover:border-amber-200 transition-all group flex flex-col justify-between gap-6">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-50 text-amber-500 rounded-xl flex items-center justify-center font-black shadow-sm">
                    {p.quantityMaintenance}
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-black text-secondary uppercase tracking-tight text-sm truncate">{p.name}</h4>
                    <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mt-1">SKU: {p.sku}</p>
                  </div>
                </div>
                <Badge className="bg-amber-50 text-amber-600 border-none text-[8px] px-2">REVISÃO</Badge>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-gray-50 gap-4">
                 <div className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase">
                    <Droplets size={14} className="text-blue-400" /> Higienização
                 </div>
                 <button 
                  disabled={isProcessing === p.id}
                  onClick={() => {
                    setIsProcessing(p.id);
                    setTimeout(() => { onReleaseFromMaintenance(p.id, p.quantityMaintenance); setIsProcessing(null); }, 800);
                  }}
                  className="w-full sm:w-auto bg-green-500 text-white px-6 py-3 rounded-xl font-black text-[9px] uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg disabled:opacity-50"
                 >
                   {isProcessing === p.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                   Liberar
                 </button>
              </div>
            </div>
          )) : (
            <div className="col-span-full py-20 text-center space-y-4 opacity-30">
               <ShieldCheck size={48} className="mx-auto" />
               <p className="text-xs font-black uppercase">Tudo em dia na oficina</p>
            </div>
          )}
        </div>
      </Card>

      {isReportModalOpen && (
        <div className="fixed inset-0 bg-secondary/95 backdrop-blur-xl z-[500] flex items-center justify-center p-4">
          <div className="bg-white rounded-[40px] w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-6 bg-[#0f172a] text-white flex justify-between items-center">
              <div className="flex items-center gap-3">
                <FileText size={24} className="text-primary" />
                <h2 className="text-xl font-black uppercase italic">Dossiê Técnico</h2>
              </div>
              <button onClick={() => setIsReportModalOpen(false)} className="p-2 hover:bg-white/10 rounded-xl transition-all"><X size={24} /></button>
            </div>

            <div className="p-6 md:p-10 overflow-y-auto scrollbar-hide flex-1 space-y-8">
               <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <ReportBox label="Auditados" value="139" />
                  <ReportBox label="Aprovação" value="94%" />
                  <ReportBox label="SLA" value="18m" />
                  <ReportBox label="Reparos" value="12" />
               </div>

               <div className="overflow-x-auto rounded-3xl border border-gray-100 shadow-sm bg-white">
                  <table className="w-full text-left min-w-[500px]">
                    <thead className="bg-gray-50 text-[9px] font-black text-gray-400 uppercase tracking-widest">
                       <tr>
                          <th className="px-6 py-4">Material</th>
                          <th className="px-6 py-4 text-right">Status</th>
                       </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                       {[1,2,3].map(i => (
                         <tr key={i}>
                            <td className="px-6 py-5">
                               <p className="text-xs font-black text-secondary uppercase">Equipamento Estrutural 0{i}</p>
                               <p className="text-[10px] text-gray-400 font-bold uppercase">Higienização Padrão</p>
                            </td>
                            <td className="px-6 py-5 text-right">
                               <Badge className="bg-green-50 text-green-600 border-none font-black">OK</Badge>
                            </td>
                         </tr>
                       ))}
                    </tbody>
                  </table>
               </div>
            </div>

            <div className="p-8 bg-gray-50 border-t border-gray-100 flex justify-end shrink-0">
               <button onClick={() => setIsReportModalOpen(false)} className="w-full sm:w-auto px-10 py-4 bg-secondary text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl">Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ReportBox = ({ label, value }: any) => (
  <div className="bg-white border border-gray-100 p-5 rounded-3xl shadow-sm text-center">
     <p className="text-[8px] font-black text-gray-300 uppercase tracking-widest mb-1">{label}</p>
     <p className="text-xl font-black text-secondary tracking-tighter">{value}</p>
  </div>
);